import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  Link,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { useLocalization } from '@/contexts/LocalizationContext';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/api';
import MembershipCard from '../components/MembershipCard';
import { AuthShell } from '@/components/AuthShell';

interface RegisterSuccessProps {
  onContinue: () => void;
}

const SuccessMessageContent = ({
  handleContinue,
}: {
  handleContinue: () => void;
}) => {
  const { t } = useLocalization();
  const { storeSettings } = useStoreSettings();
  const { customer } = useAuth();
  const pointsEnabled = storeSettings?.pointsEnabled !== false;
  const theme = useTheme();

  // Detect device OS
  const detectOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    if (/android/i.test(userAgent)) {
      return 'android';
    }
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return 'ios';
    }
    
    if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) {
      return 'macos';
    }
    
    return 'other';
  };

  const deviceOS = detectOS();
  const isAppleDevice = deviceOS === 'ios' || deviceOS === 'macos';
  const isAndroidDevice = deviceOS === 'android';

  const handleAddToAppleWallet = async () => {
    console.log("Adding to Apple Wallet");
    try {
      const response = await api.get("/api/wallet/apple-pass", {
        responseType: "blob",
        headers: {
          Accept: "application/vnd.apple.pkpass",
        },
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "membership.pkpass";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Apple Wallet pass:", error);
    }
  };

  const handleAddToAndroidWallet = async () => {
    console.log("Adding to Android Wallet");
    try {
      const { data } = await api.post<{ saveUrl: string }>(
        "/api/wallet/google/save-link"
      );
      window.open(data.saveUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error adding to Android Wallet:", error);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', px: 4, py: 5 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#333',
          fontSize: '24px',
        }}
      >
        {t('auth.registerSuccess.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: '#666',
          mb: 4,
          lineHeight: 1.6,
          fontSize: '16px',
        }}
      >
        {t('auth.registerSuccess.subtitle')}
      </Typography>


      {customer && (
        <Box sx={{ mb: 3, maxWidth: '300px', mx: 'auto' }}>
          <MembershipCard
            user={customer}
            showQR={false}
            showMembershipLevel={true}
            showPoints={pointsEnabled}
            expiringPoints={null}
            membershipCardImage={
              storeSettings?.memberships?.find(
                ml => ml.name === customer?.membershipLevelName
              )?.cardImageUrl || '/icons/ic_member_card.svg'
            }
          />
        </Box>
      )}

      <Typography
        variant="h4"
        component="h2"
        sx={{
          fontWeight: 600,
          mb: 2,
          color: '#333',
          fontSize: '20px',
          // pt: 1,
        }}
      >
        {t('auth.registerSuccess.addToWalletPass')}
      </Typography>

      {(isAppleDevice || deviceOS === 'other') && (
        <Box>
          <Link type="button" component="button" sx={{ mb: 2.5 }} onClick={handleAddToAppleWallet}>
            <img src="/icons/ic_apple_wallet.svg" alt="Add to Apple Wallet" width={142} />
          </Link>
        </Box>
      )}

      {(isAndroidDevice) && (
        <Box>
          <Link type="button" component="button" sx={{ mb: 2.5 }} onClick={handleAddToAndroidWallet}>
            <img src="/icons/ic_google_wallet.svg" alt="Add to Google Wallet" width={198} />
          </Link>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#3d3d3d',
            mb: 0.5,
          }}
        >
          {t('auth.registerSuccess.androidDevice')}
        </Typography>
        <Link
          type="button"
          component="button"
          variant="body2"
          onClick={() => {
            if (isAndroidDevice) {
              handleAddToAndroidWallet();
            } else {
              handleAddToAppleWallet();
            }
          }}
          sx={{
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {t('auth.registerSuccess.pleaseSwitchHere')}
        </Link>
      </Box>



      {/* Skip Button */}
      <Button
        variant="text"
        size="large"
        onClick={handleContinue}
        sx={{ color: '#676767' }}
      >
        <span>{t('common.skip')}</span>
        <ArrowForward sx={{ ml: 0.5 }} />
      </Button>
    </Box>
  )
}


export default function RegisterSuccess({ onContinue }: RegisterSuccessProps) {
  const handleContinue = () => onContinue();

  return (
    <AuthShell showLangSelector={false} cardPaddingTop={3}>
      <SuccessMessageContent handleContinue={handleContinue} />
    </AuthShell>
  );
}
